import { useEffect, useRef, type ReactElement } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   L'écran d'options.

   Deux principes le structurent.

   1. Les réglages de CONFORT vont jusqu'à ZÉRO (PLAN.md §4.6). Secousse et
      flash désactivables entièrement, pas « atténués ». Un curseur qui
      s'arrête à 30 % ne sert à rien pour qui en a réellement besoin : c'est la
      différence entre une option d'accessibilité et une option de goût.

   2. Ce qui touche à la LISIBILITÉ n'est pas réglable. Le point de hitbox, la
      palette des projectiles ennemis, le télégraphe cyan : aucun de ces
      éléments n'apparaît ici. Les rendre optionnels reviendrait à laisser un
      joueur désactiver sa propre capacité à comprendre ce qui le tue.

   L'écran ne s'abonne à rien : il est monté hors du jeu, il lit un objet et
   remonte les changements. Aucune de ses valeurs ne change à 60 Hz.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface OptionsData {
  shake: number;
  flash: number;
  master: number;
  music: number;
  sfx: number;
  /** Mode de compensation du daltonisme — une valeur de `Colorblind` (0 = aucun).
   *  Réglage de LISIBILITÉ, pas de goût : il aide à distinguer le danger. */
  colorblind: number;
  /** Vitesse de jeu, ×0,5 à ×1,5 (1 = normale). Aide d'accessibilité : ralentir
   *  laisse le temps de lire un rideau. La simulation reste déterministe. */
  gameSpeed: number;
}

/** Libellés des modes de daltonisme, dans l'ordre de l'enum `Colorblind`. */
const COLORBLIND_LABELS = ['Aucun', 'Protanopie', 'Deutéranopie', 'Tritanopie', 'Renforcé'] as const;

function Slider({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
}): ReactElement {
  return (
    <label className="opt">
      <span className="opt-label">{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {/* La valeur est affichée en pour-cent : « 0 % » est une information
          bien plus claire que la position d'un curseur, en particulier pour
          confirmer qu'un effet est réellement coupé. */}
      <span className="opt-value">{Math.round(value * 100)} %</span>
      {hint && <span className="opt-hint">{hint}</span>}
    </label>
  );
}

export function OptionsScreen({
  data,
  onChange,
  onBack,
}: {
  data: OptionsData;
  onChange: (next: OptionsData) => void;
  onBack: () => void;
}): ReactElement {
  const set = (k: keyof OptionsData) => (v: number) => onChange({ ...data, [k]: v });

  /* Le focus initial va sur RETOUR, et non sur le premier curseur comme dans
     les autres écrans. La raison est que les flèches et le stick pilotent un
     `input[range]` dès qu'il a le focus : arriver sur cet écran et effleurer
     une direction modifierait la secousse d'écran sans que le joueur l'ait
     demandé. Un focus initial doit être inoffensif. */
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    back.current?.focus();
  }, []);

  return (
    <div className="screen options">
      <h2>OPTIONS</h2>

      <div className="opt-group">
        <h3>CONFORT</h3>
        <Slider
          label="Secousse d'écran"
          hint="0 % supprime toute secousse. Aucune information de jeu n'y est portée."
          value={data.shake}
          onChange={set('shake')}
        />
        <Slider
          label="Flashs"
          hint="0 % supprime le voile blanc des coups encaissés."
          value={data.flash}
          onChange={set('flash')}
        />
      </div>

      <div className="opt-group">
        <h3>ACCESSIBILITÉ</h3>
        {/* Le DALTONISME est une exception assumée au principe « la lisibilité ne
            se règle pas » : ici on ne CACHE pas l'information de danger, on la rend
            LISIBLE pour un œil qui, sans ça, confondrait le rouge du danger avec
            l'orange du décor. */}
        <label className="opt">
          <span className="opt-label">Daltonisme</span>
          <select
            className="opt-select"
            value={data.colorblind}
            onChange={(e) => set('colorblind')(Number(e.target.value))}
          >
            {COLORBLIND_LABELS.map((n, i) => (
              <option key={n} value={i}>
                {n}
              </option>
            ))}
          </select>
          <span className="opt-hint">
            Sépare le danger (rouge/rose/violet) du décor et des explosions (orange).
          </span>
        </label>
        <label className="opt">
          <span className="opt-label">Vitesse de jeu</span>
          <input
            type="range"
            min={0.5}
            max={1.5}
            step={0.05}
            value={data.gameSpeed}
            onChange={(e) => set('gameSpeed')(Number(e.target.value))}
          />
          <span className="opt-value">×{data.gameSpeed.toFixed(2)}</span>
          <span className="opt-hint">
            Ralentit ou accélère toute la partie — le temps de lire un rideau. La
            difficulté suit : c'est une aide, pas un réglage de score.
          </span>
        </label>
      </div>

      <div className="opt-group">
        <h3>SON</h3>
        <Slider label="Volume général" value={data.master} onChange={set('master')} />
        <Slider label="Musique" value={data.music} onChange={set('music')} />
        <Slider label="Effets" value={data.sfx} onChange={set('sfx')} />
      </div>

      <p className="opt-note">
        Le point de hitbox, la couleur des projectiles ennemis et le télégraphe cyan ne sont pas
        réglables : ils portent l'information qui rend le jeu jouable.
      </p>

      <button ref={back} className="btn btn-primary" onClick={onBack}>
        RETOUR
      </button>
    </div>
  );
}
